import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  CalendarIcon,
  UserIcon,
  UserGroupIcon,
  StarIcon,
  BuildingStorefrontIcon,
  GlobeAsiaAustraliaIcon,
  BuildingOfficeIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ScaleIcon,
  AdjustmentsHorizontalIcon,
  DocumentArrowDownIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { format, subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { generateClient } from 'aws-amplify/api';
import { withAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getUsersData, buildTree } from '../data/users';
import { fetchSelf } from '../services/userSelf';
import useTranslation from '../services/translationService';
import SportsSettlementAuditModal from '../components/SportsSettlementAuditModal';
import {
  MAX_AUDIT_PLAYERS,
  findNodeInTree,
  getAllPlayersUnderNode
} from '../utils/sportsSettlementAudit';

const client = generateClient();

const ITEMS_PER_PAGE = 15;

const formatAmount = (amount, currency) => {
  // Convert amount to number if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: String(currency || '').toUpperCase() === 'KWD' ? 3 : 2,
  }).format(numericAmount);
};

// Compact currency formatting (e.g. "LBP 1.2B", "$234M") for places where
// space is tight, like the hero summary cards. Used for currencies that can
// reach billions (e.g. LBP) so the value still fits on a phone screen.
// Uses the original sign convention. The full value is shown via title= so
// users can tap-and-hold to see the exact amount.
const formatCompactAmount = (amount, currency) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Math.abs(numericAmount) >= 10000) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(numericAmount);
    } catch {
      // Fall through to standard formatting if browser doesn't support compact
    }
  }
  return formatAmount(amount, currency);
};

// Full number formatting with grouping commas but no currency symbol. Used in
// summary cards where the currency is already shown via the currency tabs /
// footer, so we can dedicate the available space to the digits themselves.
// This lets users see exact amounts like "1,482,382,000" instead of "1.4B".
const formatSummaryAmount = (amount, currency) => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: String(currency || '').toUpperCase() === 'KWD' ? 3 : 2,
  }).format(numericAmount);
};

// Concrete dates for each date-range preset. Kept at module scope so the
// picker, the transaction query and the sports settlement audit all describe
// exactly the same period.
const DATE_RANGE_GETTERS = {
  today: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }),
  week: () => ({
    start: startOfDay(startOfWeek(new Date(), { weekStartsOn: 1 })),
    end: endOfDay(endOfWeek(new Date(), { weekStartsOn: 1 }))
  }),
  month: () => ({ start: startOfDay(startOfMonth(new Date())), end: endOfDay(endOfMonth(new Date())) }),
  lastMonth: () => ({
    start: startOfDay(startOfMonth(subMonths(new Date(), 1))),
    end: endOfDay(endOfMonth(subMonths(new Date(), 1)))
  }),
  twoMonthsAgo: () => ({
    start: startOfDay(startOfMonth(subMonths(new Date(), 2))),
    end: endOfDay(endOfMonth(subMonths(new Date(), 2)))
  }),
  last7: () => ({ start: startOfDay(subDays(new Date(), 6)), end: endOfDay(new Date()) }),
  last30: () => ({ start: startOfDay(subDays(new Date(), 29)), end: endOfDay(new Date()) })
};

const resolveDateRange = (rangeValue, customRange) => {
  if (rangeValue === 'custom' && customRange?.start && customRange?.end) {
    return { start: startOfDay(customRange.start), end: endOfDay(customRange.end) };
  }
  const getRange = DATE_RANGE_GETTERS[rangeValue];
  if (getRange) return getRange();
  return DATE_RANGE_GETTERS.last30();
};

const formatDate = (timestamp) => {
  return format(new Date(timestamp), 'MMM d, HH:mm');
};

const formatFullDate = (timestamp) => {
  return format(new Date(timestamp), 'MMM d, yyyy HH:mm:ss');
};

const formatUserType = (type) => {
  if (!type) return 'Unknown';
  // Convert types like 'super-agent' to 'Super Agent'
  return type.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

// Rank hierarchy (higher number = higher rank)
const getRankLevel = (type) => {
  const ranks = {
    'admin': 5,
    'country': 4,
    'city': 3,
    'super-agent': 2,
    'agent': 1,
    'player': 0
  };
  return ranks[type] || 0;
};

// Check if a user has higher rank than the current user
const isHigherRank = (userType, currentUserType) => {
  return getRankLevel(userType) > getRankLevel(currentUserType);
};

// Get display name based on rank comparison
// TEMPORARILY DISABLED: Always show actual username regardless of rank
const getDisplayName = (username, agentsInUserTree, currentUserRole) => {
  return username;
};

const TransactionModal = ({ transaction, onClose, agentsInUserTree = [], userRole = null }) => {
  const { t } = useTranslation();
  if (!transaction) return null;
  const isWithdrawal = transaction.isWithdrawal;
  
  // Find the target user in the tree
  const targetUser = agentsInUserTree.find(user => user.username === transaction.targetAgent);
  // Check if target is an agent (not a player) - agents include country, city, super-agent, agent
  const targetIsAgent = targetUser && targetUser.type !== 'player';
  
  // Get display names based on rank
  const initiatorDisplayName = getDisplayName(transaction.initiator, agentsInUserTree, userRole);
  const targetDisplayName = getDisplayName(transaction.targetAgent, agentsInUserTree, userRole);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
        isWithdrawal 
          ? 'bg-red-50 border border-red-100' 
          : 'bg-emerald-50 border border-emerald-100'
      }`}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className={`text-xl font-bold ${
              isWithdrawal ? 'text-red-800' : 'text-emerald-800'
            }`}>
              {t('transactions.details', 'Transaction Details')}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">{t('transactions.transactionId', 'Transaction ID')}</div>
                <div className="mt-1 text-sm">{transaction.id}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">{t('transactions.dateTime', 'Date & Time')}</div>
                <div className="mt-1 text-sm">{formatFullDate(transaction.timestamp)}</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">{t('transactions.from', 'From')}</div>
                <div className="mt-1 text-sm">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    {initiatorDisplayName === transaction.initiator ? '@' : ''}{initiatorDisplayName}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">{t('transactions.to', 'To')}</div>
                <div className="mt-1 text-sm flex items-center gap-2">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                    {targetDisplayName === transaction.targetAgent ? '@' : ''}{targetDisplayName}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                    targetIsAgent 
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' 
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                  }`}>
                    {targetIsAgent ? (
                      <>
                        <UserGroupIcon className="w-3.5 h-3.5" />
                        {targetUser ? formatUserType(targetUser.type) : t('users.agent', 'Agent')}
                      </>
                    ) : (
                      <>
                        <UserIcon className="w-3.5 h-3.5" />
                        {targetUser?.type === 'player' ? t('users.player', 'Player') : t('users.playerExternal', 'Player (External)')}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">{t('transactions.amount', 'Amount')}</div>
              <div className={`mt-1 text-2xl font-bold ${
                isWithdrawal ? 'text-red-600' : 'text-emerald-600'
              }`}>
                <span className="text-3xl">{isWithdrawal ? '-' : '+'}</span>{formatAmount(transaction.amount, transaction.currency)}
              </div>
            </div>

            {/* Target Balance (Privacy: Only show target balance, not initiator) */}
            <div className="space-y-4">
              <div className="max-w-md mx-auto">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                    {targetDisplayName === transaction.targetAgent ? '@' : ''}{targetDisplayName} {t('wallet.balance', 'Balance')}
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{t('transactions.before', 'Before')}:</span>
                      <span className="font-medium">{formatAmount(transaction.targetBalance, transaction.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{t('transactions.after', 'After')}:</span>
                      <span className="font-medium">
                        {formatAmount(
                          isWithdrawal 
                            ? transaction.targetBalance - transaction.amount 
                            : transaction.targetBalance + transaction.amount, 
                          transaction.currency
                        )}
                      </span>
                    </div>
                    <div className="h-px bg-gray-200 dark:bg-gray-600 my-1"></div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{t('transactions.change', 'Change')}:</span>
                      <span className={`font-medium ${isWithdrawal ? 'text-red-600' : 'text-emerald-600'}`}>
                        {isWithdrawal ? '-' : '+'}{formatAmount(transaction.amount, transaction.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">{t('transactions.type', 'Type')}</div>
              <div className="mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  isWithdrawal 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {isWithdrawal ? t('wallet.withdraw', 'Withdrawal') : t('wallet.deposit', 'Deposit')}
                </span>
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500">{t('transactions.notes', 'Notes')}</div>
              <div className="mt-1 text-sm">
                {transaction.notes || t('transactions.noNotes', 'No notes provided')}
              </div>
            </div>
            
            {transaction.isTestTransaction && (
              <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded border border-purple-200 dark:border-purple-800">
                <p className="text-purple-800 dark:text-purple-300 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('transactions.testTransactionLabel', 'Test Transaction')}
                </p>
              </div>
            )}
            
            {(transaction.isAdminSelfTransaction || transaction.isAdminToAdminTransaction) && (
              <div className="bg-yellow-50 p-2 rounded">
                <p className="text-yellow-800 text-sm">
                  ⚠️ {t('transactions.adminTransaction', 'Admin Transaction')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TransactionCard = ({ transaction, onClick, agentsInUserTree = [], userRole = null }) => {
  const { t } = useTranslation();
  const isWithdrawal = transaction.isWithdrawal;
  
  // Find the target user in the tree
  const targetUser = agentsInUserTree.find(user => user.username === transaction.targetAgent);
  // Check if target is an agent (not a player) - agents include country, city, super-agent, agent
  const targetIsAgent = targetUser && targetUser.type !== 'player';
  
  // Get display names based on rank
  const initiatorDisplayName = getDisplayName(transaction.initiator, agentsInUserTree, userRole);
  const targetDisplayName = getDisplayName(transaction.targetAgent, agentsInUserTree, userRole);
  
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(transaction);
        }
      }}
      onClick={() => onClick(transaction)}
      className={`group relative px-3 py-2.5 cursor-pointer rounded-xl border transition-all duration-150 active:scale-[0.99] hover:shadow-sm ${
        isWithdrawal
          ? 'bg-white border-red-100 hover:border-red-200 dark:bg-gray-800 dark:border-red-900/40 dark:hover:border-red-800'
          : 'bg-white border-emerald-100 hover:border-emerald-200 dark:bg-gray-800 dark:border-emerald-900/40 dark:hover:border-emerald-800'
      }`}
    >
      {/* Colored side accent bar */}
      <span
        className={`absolute left-0 top-2 bottom-2 w-1 rounded-full ${
          isWithdrawal ? 'bg-red-400 dark:bg-red-500' : 'bg-emerald-400 dark:bg-emerald-500'
        }`}
      />

      <div className="flex items-center gap-3 pl-2">
        {/* Big +/- amount - mobile-optimized for at-a-glance scanning */}
        <div className="flex-shrink-0 min-w-0">
          <div
            title={`${isWithdrawal ? '-' : '+'}${formatAmount(transaction.amount, transaction.currency)}`}
            className={`text-base sm:text-lg font-bold leading-tight tabular-nums whitespace-nowrap ${
              isWithdrawal
                ? 'text-red-600 dark:text-red-400'
                : 'text-emerald-600 dark:text-emerald-400'
            }`}
          >
            {isWithdrawal ? '−' : '+'}
            {formatCompactAmount(transaction.amount, transaction.currency)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {formatDate(transaction.timestamp)}
          </div>
        </div>

        {/* Middle: From -> To info. Names wrap and break inside the word
            instead of getting cut off with ellipsis on long usernames. */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-gray-700 dark:text-gray-300">
            <span
              title={`@${initiatorDisplayName}`}
              className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium break-all"
            >
              @{initiatorDisplayName}
            </span>
            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">→</span>
            <span
              title={`@${targetDisplayName}`}
              className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium break-all inline-flex items-center gap-1"
            >
              <span className="break-all">@{targetDisplayName}</span>
              {targetIsAgent ? (
                <UserGroupIcon className="w-3 h-3 flex-shrink-0 opacity-70" />
              ) : (
                <UserIcon className="w-3 h-3 flex-shrink-0 opacity-70" />
              )}
            </span>
          </div>
          {transaction.notes && (
            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
              {transaction.notes}
            </div>
          )}
        </div>

        {transaction.isTestTransaction && (
          <span
            className="flex-shrink-0 w-2 h-2 rounded-full bg-purple-500 dark:bg-purple-400 animate-pulse"
            title="Test Transaction"
          />
        )}
        <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
      </div>
    </div>
  );
};

const DateRangePicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate the month name for "2 months ago"
  const now = new Date();
  const twoMonthsAgoDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const twoMonthsAgoMonthName = format(twoMonthsAgoDate, 'MMMM');

  const quickRanges = [
    { label: t('dateRange.today', 'Today'), value: 'today', getRange: DATE_RANGE_GETTERS.today },
    { label: t('dateRange.thisWeek', 'This Week'), value: 'week', getRange: DATE_RANGE_GETTERS.week },
    { label: t('dateRange.thisMonth', 'This Month'), value: 'month', getRange: DATE_RANGE_GETTERS.month },
    { label: t('dateRange.lastMonth', 'Last Month'), value: 'lastMonth', getRange: DATE_RANGE_GETTERS.lastMonth },
    { label: `2 Months Ago (${twoMonthsAgoMonthName})`, value: 'twoMonthsAgo', getRange: DATE_RANGE_GETTERS.twoMonthsAgo },
    { label: t('dateRange.last7Days', 'Last 7 Days'), value: 'last7', getRange: DATE_RANGE_GETTERS.last7 },
    { label: t('dateRange.last30Days', 'Last 30 Days'), value: 'last30', getRange: DATE_RANGE_GETTERS.last30 },
    { label: t('dateRange.customRange', 'Custom Range'), value: 'custom', getRange: () => ({ start: null, end: null }) }
  ];

  // Initialize dates based on the current value
  useEffect(() => {
    if (value === 'custom' && startDate && endDate) {
      // Keep existing custom range
      return;
    }
    
    const range = quickRanges.find(r => r.value === value);
    if (range) {
      const { start, end } = range.getRange();
      setStartDate(start);
      setEndDate(end);
    }
  }, [value]);

  const handleQuickSelect = (range) => {
    if (range.value === 'custom') {
      setStartDate(null);
      setEndDate(null);
      onChange('custom');
    } else {
      const { start, end } = range.getRange();
      setStartDate(start);
      setEndDate(end);
      onChange(range.value);
    }
    setIsOpen(false);
  };

  const handleDateClick = (date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else if (date < startDate) {
      setStartDate(date);
      setEndDate(startDate);
    } else {
      setEndDate(date);
      onChange({ start: startDate, end: date });
      setIsOpen(false);
    }
  };

  // Calculate popup position
  const updatePosition = (buttonRef) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      
      // Check if there's enough space below
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Check if there's enough space on the right
      const spaceRight = windowWidth - rect.left;
      
      setPosition({
        top: spaceBelow > 400 ? rect.bottom + 8 : rect.top - 408, // 400px for content + 8px gap
        left: spaceRight > 320 ? rect.left : rect.right - 320, // 320px for content
      });
    }
  };

  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      updatePosition(buttonRef);
      // Update position on window resize
      const handleResize = () => updatePosition(buttonRef);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen]);

  const generateCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const lastDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const startingDay = firstDayOfMonth.getDay();
    
    // Add previous month's days
    for (let i = 0; i < startingDay; i++) {
      const date = new Date(firstDayOfMonth);
      date.setDate(date.getDate() - (startingDay - i));
      days.push({ date, isCurrentMonth: false });
    }
    
    // Add current month's days
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), i);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Add next month's days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(lastDayOfMonth);
      date.setDate(date.getDate() + i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  };

  const isInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartDate = (date) => startDate && isSameDay(date, startDate);
  const isEndDate = (date) => endDate && isSameDay(date, endDate);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full md:w-auto"
      >
        <CalendarIcon className="w-4 h-4" />
        <span>
          {value === 'custom' && startDate && endDate
            ? `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
            : quickRanges.find(r => r.value === value)?.label || t('dateRange.selectDateRange', 'Select Date Range')}
        </span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popup */}
          <div 
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-[320px]"
          >
            <div className="p-4">
              {/* Close button */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {quickRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => handleQuickSelect(range)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      value === range.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1))}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    title={t('dateRange.previousMonth', 'Previous month')}
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <span className="font-medium">
                    {format(viewDate, 'MMMM yyyy')}
                  </span>
                  <button
                    onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1))}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    title={t('dateRange.nextMonth', 'Next month')}
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {[
                    t('dateRange.weekdays.su', 'Su'), 
                    t('dateRange.weekdays.mo', 'Mo'), 
                    t('dateRange.weekdays.tu', 'Tu'), 
                    t('dateRange.weekdays.we', 'We'), 
                    t('dateRange.weekdays.th', 'Th'), 
                    t('dateRange.weekdays.fr', 'Fr'), 
                    t('dateRange.weekdays.sa', 'Sa')
                  ].map((day) => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
                      {day}
                    </div>
                  ))}
                  {generateCalendarDays().map(({ date, isCurrentMonth }, index) => (
                    <button
                      key={index}
                      onClick={() => handleDateClick(date)}
                      onMouseEnter={() => setHoverDate(date)}
                      className={`p-1 text-sm rounded-lg transition-colors ${
                        !isCurrentMonth
                          ? 'text-gray-400 dark:text-gray-500'
                          : isInRange(date)
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      } ${
                        isStartDate(date) || isEndDate(date)
                          ? 'bg-primary-500 text-white'
                          : ''
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const getUserIcon = (type) => {
  switch (type) {
    case 'admin':
      return BuildingOfficeIcon;
    case 'country':
      return GlobeAsiaAustraliaIcon;
    case 'city':
      return BuildingStorefrontIcon;
    case 'super-agent':
      return StarIcon;
    case 'agent':
      return UserGroupIcon;
    default:
      return UserIcon;
  }
};

const getUserStyle = (type) => {
  switch (type) {
    case 'admin':
      return 'text-purple-500';
    case 'country':
      return 'text-blue-500';
    case 'city':
      return 'text-green-500';
    case 'super-agent':
      return 'text-orange-500';
    case 'agent':
      return 'text-yellow-500';
    default:
      return 'text-gray-400';
  }
};

const AgentFilter = ({ label, value, agents, onChange, onClear }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(value);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const inputRef = useRef(null);

  // Update selectedAgent when value changes
  useEffect(() => {
    setSelectedAgent(value);
  }, [value]);

  // Transform agents into the format we need
  const formattedAgents = useMemo(() => {
    if (!agents || !Array.isArray(agents)) return [];
    
    return agents.map(agent => ({
      id: agent.id,
      name: agent.name || agent.username || t('common.unknown', 'Unknown'),
      username: agent.username || 'unknown',
      type: agent.type || 'agent',
      balances: agent.balances || {},
      children: agent.children || []
    }));
  }, [agents, t]);

  const filteredAgents = useMemo(() => {
    if (!formattedAgents.length) return [];
    
    // Return empty array if search query is less than 3 characters
    if (searchQuery.length < 3) {
      return [];
    }
    
    return formattedAgents
      .filter(agent => {
        const searchLower = searchQuery.toLowerCase();
        return agent.name.toLowerCase().includes(searchLower) || 
               agent.username.toLowerCase().includes(searchLower);
      })
      .sort((a, b) => {
        // Sort by username length (shortest first)
        const usernameA = a.username || '';
        const usernameB = b.username || '';
        return usernameA.length - usernameB.length;
      });
  }, [formattedAgents, searchQuery]);

  const paginatedAgents = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredAgents.slice(start, start + itemsPerPage);
  }, [filteredAgents, page]);

  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (agent) => {
    setSelectedAgent(agent);
    onChange(agent);
    setIsOpen(false);
    setSearchQuery('');
    setPage(1);
  };

  const handleClear = () => {
    setSelectedAgent(null);
    onClear();
    setIsOpen(false);
    setSearchQuery('');
    setPage(1);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={selectedAgent ? `@${selectedAgent.username}${selectedAgent.name ? ` (${selectedAgent.name})` : ''}` : undefined}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full min-w-0"
      >
        <UserIcon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 min-w-0 text-left">
          {selectedAgent ? (
            <span className="block truncate font-medium">
              @{selectedAgent.username}
              {selectedAgent.name && (
                <span className="text-gray-500 dark:text-gray-400 text-xs font-normal ml-1">
                  ({selectedAgent.name})
                </span>
              )}
            </span>
          ) : (
            <span className="block truncate">{label}</span>
          )}
        </span>
        {selectedAgent && (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClear();
            }}
            className="p-1 flex-shrink-0 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full cursor-pointer"
          >
            <XMarkIcon className="w-4 h-4" />
          </div>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Popup */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-[400px] max-h-[80vh] overflow-hidden">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {label}
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Search */}
              <div className="relative mb-6">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={t('transactions.searchAgentPlaceholder', 'Search by name or username...')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Results Count */}
              {searchQuery.length >= 3 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {filteredAgents.length} {t('users.agent', {count: filteredAgents.length}, 'agent')}
                  {filteredAgents.length !== 1 ? 's' : ''} {t('common.found', 'found')}
                </div>
              )}

              {/* Agent List */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
                {searchQuery.length < 3 ? (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {t('transactions.minSearchChars', 'Type at least 3 characters to search')}
                  </div>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => handleSelect(agent)}
                      className={`w-full px-3 py-2 text-left rounded-lg transition-colors ${
                        selectedAgent?.id === agent.id
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {React.createElement(getUserIcon(agent.type), {
                          className: `h-5 w-5 ${getUserStyle(agent.type)}`
                        })}
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{agent.name}</span>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">@{agent.username}</span>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    {t('users.noUsers', 'No agents found')}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {searchQuery.length >= 3 && totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('common.page', 'Page')} {page} {t('common.of', 'of')} {totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Compact summary card always visible at the top of the page. Shows the three
// numbers users care about most: Deposits, Withdrawals, Net. Tapping a card
// filters the list, so the most common filter actions don't need the filters
// panel. A row of currency tabs appears only when results contain more than
// one currency. A second row of "All / To Agent / To Player" segmented pills
// surfaces the second-most-asked filter without opening the filters panel.
const HeroSummary = ({
  t,
  byCurrency,
  activeCurrency,
  onCurrencyChange,
  typeFilter,
  onTypeFilterChange,
  targetTypeFilter,
  onTargetTypeFilterChange,
  totalCount,
}) => {
  const currencies = Object.keys(byCurrency);
  const hasData = currencies.length > 0;

  const displayCurrency =
    activeCurrency && byCurrency[activeCurrency]
      ? activeCurrency
      : currencies[0];
  const data = displayCurrency
    ? byCurrency[displayCurrency]
    : { deposits: 0, withdrawals: 0, depositCount: 0, withdrawalCount: 0, net: 0 };

  const netPositive = data.net >= 0;

  // Segmented pill row. Each option can declare its own `activeClass` so the
  // active state can be color-coded (blue for agents, purple for players),
  // making it obvious which filter is on without reading the label.
  const Segmented = ({ options, value, onChange, ariaLabel, defaultValue = 'all' }) => {
    const isFilterActive = value !== defaultValue;
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`inline-flex items-center rounded-full p-0.5 w-full transition-colors ${
          isFilterActive
            ? 'bg-gray-200 dark:bg-gray-700 ring-1 ring-gray-300 dark:ring-gray-600'
            : 'bg-gray-100 dark:bg-gray-700/60'
        }`}
      >
        {options.map((opt) => {
          const isActive = opt.value === value;
          const activeClass =
            opt.activeClass || 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white';
          return (
            <button
              key={opt.value}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(opt.value)}
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap ${
                isActive
                  ? `${activeClass} shadow-sm`
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {opt.icon ? <opt.icon className="w-3.5 h-3.5" /> : null}
              <span>{opt.label}</span>
              {isActive && opt.value !== defaultValue && (
                <span className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-current opacity-80" />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const targetOptions = [
    { value: 'all', label: t('common.all', 'All') },
    {
      value: 'agent',
      label: t('transactions.targetAgent', 'To Agent'),
      icon: UserGroupIcon,
      activeClass: 'bg-blue-500 text-white dark:bg-blue-600',
    },
    {
      value: 'player',
      label: t('transactions.targetPlayer', 'To Player'),
      icon: UserIcon,
      activeClass: 'bg-purple-500 text-white dark:bg-purple-600',
    },
  ];

  if (!hasData) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <Segmented
          options={targetOptions}
          value={targetTypeFilter}
          onChange={onTargetTypeFilterChange}
          ariaLabel={t('transactions.targetTypeFilter', 'Target type filter')}
        />
        <div className="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('transactions.noResultsForSearch', 'No transactions match your search')}
        </div>
      </div>
    );
  }

  // Number-card sub-component. Compact, clickable, color-coded.
  // `amount` is the formatted string shown; `fullAmount` is the exact value
  // shown on hover so users with billion-scale currencies don't lose precision.
  // On mobile each card takes a full row (horizontal layout: label+count
  // on the left, big amount on the right) so even billion-scale LBP figures
  // get the whole screen width to render in. On sm+ the cards flip back to
  // the original vertical 3-column layout.
  const TypeCard = ({ label, amount, fullAmount, count, accent, active, onClick }) => {
    // Length-based responsive sizing for the sm+ breakpoint (where the card
    // sits in a 3-column grid and horizontal space is tight). On mobile we
    // use a large, fixed size since the card has the full row to itself.
    const amountLength = (amount || '').length;
    let smAmountSizeClass;
    if (amountLength <= 7) {
      smAmountSizeClass = 'sm:text-xl md:text-2xl';
    } else if (amountLength <= 10) {
      smAmountSizeClass = 'sm:text-lg md:text-xl';
    } else if (amountLength <= 13) {
      smAmountSizeClass = 'sm:text-base md:text-lg';
    } else {
      smAmountSizeClass = 'sm:text-sm md:text-base';
    }
    const accents = {
      green: {
        ring: 'ring-emerald-400 dark:ring-emerald-500',
        bg: active
          ? 'bg-emerald-50 dark:bg-emerald-900/30'
          : 'bg-gray-50 dark:bg-gray-700/40 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
        label: 'text-emerald-700 dark:text-emerald-300',
        big: 'text-emerald-700 dark:text-emerald-300',
      },
      red: {
        ring: 'ring-red-400 dark:ring-red-500',
        bg: active
          ? 'bg-red-50 dark:bg-red-900/30'
          : 'bg-gray-50 dark:bg-gray-700/40 hover:bg-red-50 dark:hover:bg-red-900/20',
        label: 'text-red-700 dark:text-red-300',
        big: 'text-red-700 dark:text-red-300',
      },
      net: {
        ring: netPositive
          ? 'ring-emerald-400 dark:ring-emerald-500'
          : 'ring-red-400 dark:ring-red-500',
        bg: netPositive
          ? 'bg-emerald-50 dark:bg-emerald-900/20'
          : 'bg-red-50 dark:bg-red-900/20',
        label: 'text-gray-700 dark:text-gray-300',
        big: netPositive
          ? 'text-emerald-700 dark:text-emerald-300'
          : 'text-red-700 dark:text-red-300',
      },
    };
    const a = accents[accent];

    const Wrapper = onClick ? 'button' : 'div';
    return (
      <Wrapper
        onClick={onClick}
        aria-pressed={onClick ? !!active : undefined}
        className={`w-full min-w-0 text-left rounded-xl ${a.bg} transition-all p-3 sm:p-2.5 ${
          onClick ? 'cursor-pointer active:scale-[0.98]' : ''
        } ${active ? `ring-2 ${a.ring}` : ''} flex items-center justify-between gap-3 sm:block`}
      >
        {/* Mobile: label + count stacked on the left of the row.
            sm+: label sits above the amount, count below. */}
        <div className="min-w-0 sm:contents">
          <div className={`text-[11px] font-semibold uppercase tracking-wide ${a.label}`}>
            {label}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 sm:hidden">
            {count} {t('transactions.txns', 'txns')}
          </div>
        </div>
        <div
          title={fullAmount}
          className={`font-bold leading-tight tabular-nums text-xl ${smAmountSizeClass} ${a.big} text-right sm:text-left sm:mt-0.5 truncate min-w-0`}
        >
          {amount}
        </div>
        {/* Count row for sm+ only; on mobile it's shown next to the label. */}
        <div className="hidden sm:block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          {count} {t('transactions.txns', 'txns')}
        </div>
      </Wrapper>
    );
  };

  // Show full amounts with grouping commas (e.g. "1,482,382,000" instead of
  // "1.4B") so users can read exact figures at a glance. The currency symbol
  // is intentionally omitted here because the active currency is already
  // shown in the currency tabs above and in the footer line below; this
  // frees up horizontal space for the digits. The `title` attribute carries
  // the fully-qualified amount (with currency) for hover/long-press.
  const depositsAmount = formatSummaryAmount(data.deposits, displayCurrency);
  const depositsFullAmount = formatAmount(data.deposits, displayCurrency);
  const withdrawalsAmount = formatSummaryAmount(data.withdrawals, displayCurrency);
  const withdrawalsFullAmount = formatAmount(data.withdrawals, displayCurrency);
  const netAmount = `${netPositive ? '+' : ''}${formatSummaryAmount(data.net, displayCurrency)}`;
  const netFullAmount = `${netPositive ? '+' : ''}${formatAmount(data.net, displayCurrency)}`;

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
      {/* Currency tabs only appear when results contain more than one currency */}
      {currencies.length > 1 && (
        <div className="flex items-center gap-1 px-2 pt-2 overflow-x-auto">
          {currencies.map((c) => {
            const isActive = c === displayCurrency;
            const cd = byCurrency[c];
            const cdNetPositive = cd.net >= 0;
            return (
              <button
                key={c}
                onClick={() => onCurrencyChange(c)}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>{c}</span>
                <span
                  title={`${cdNetPositive ? '+' : ''}${formatAmount(cd.net, c)}`}
                  className={`text-[10px] tabular-nums ${
                    isActive
                      ? 'opacity-80'
                      : cdNetPositive
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  {cdNetPositive ? '+' : ''}
                  {formatCompactAmount(cd.net, c)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="p-3">
        {/* Stack vertically on mobile so each card gets the full row width,
            giving billion-scale numbers enough room. Flip back to a 3-column
            grid on sm+ where horizontal space is plentiful. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <TypeCard
            label={t('transactions.deposits', 'Deposits')}
            amount={depositsAmount}
            fullAmount={depositsFullAmount}
            count={data.depositCount}
            accent="green"
            active={typeFilter === 'deposit'}
            onClick={() =>
              onTypeFilterChange(typeFilter === 'deposit' ? 'all' : 'deposit')
            }
          />
          <TypeCard
            label={t('transactions.withdrawals', 'Withdrawals')}
            amount={withdrawalsAmount}
            fullAmount={withdrawalsFullAmount}
            count={data.withdrawalCount}
            accent="red"
            active={typeFilter === 'withdrawal'}
            onClick={() =>
              onTypeFilterChange(typeFilter === 'withdrawal' ? 'all' : 'withdrawal')
            }
          />
          <TypeCard
            label={t('transactions.net', 'Net')}
            amount={netAmount}
            fullAmount={netFullAmount}
            count={data.depositCount + data.withdrawalCount}
            accent="net"
          />
        </div>

        {/* Target-type segmented pills - the second-most-asked filter, surfaced
            here so it doesn't require opening the filters panel. */}
        <div className="mt-2.5">
          <Segmented
            options={targetOptions}
            value={targetTypeFilter}
            onChange={onTargetTypeFilterChange}
            ariaLabel={t('transactions.targetTypeFilter', 'Target type filter')}
          />
        </div>

        <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
          <span>
            {totalCount} {t('transactions.transactions', 'transactions')}
            {currencies.length === 1 ? ` · ${displayCurrency}` : ''}
          </span>
          {typeFilter !== 'all' && (
            <button
              onClick={() => onTypeFilterChange('all')}
              className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              {t('transactions.showAll', 'Show all')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Transactions = () => {
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: 'month',
    currency: 'all',
    targetType: 'all', // 'all', 'agent', 'player'
    authorityType: 'all' // 'all', 'higherAuthority'
  });
  // User and agent data
  const [userInfo, setUserInfo] = useState(null);
  const [agentsInUserTree, setAgentsInUserTree] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState(['all']);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [dateRange, setDateRange] = useState('month'); // Default to this month
  const [customDateRange, setCustomDateRange] = useState(null);
  const [fromAgent, setFromAgent] = useState(null);
  const [toAgent, setToAgent] = useState(null);
  const [userTree, setUserTree] = useState(null);
  const [futureDatedWarning, setFutureDatedWarning] = useState(null);
  const [initialUserSet, setInitialUserSet] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [heroCurrency, setHeroCurrency] = useState(null);
  const [settlementAuditOpen, setSettlementAuditOpen] = useState(false);

  // Calculate the month name for "2 months ago"
  const now = new Date();
  const twoMonthsAgoDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const twoMonthsAgoMonthName = format(twoMonthsAgoDate, 'MMMM');

  // Define quickRanges at the component level
  const quickRanges = [
    { label: 'Today', value: 'today', getRange: DATE_RANGE_GETTERS.today },
    { label: 'This Week', value: 'week', getRange: DATE_RANGE_GETTERS.week },
    { label: 'This Month', value: 'month', getRange: DATE_RANGE_GETTERS.month },
    { label: 'Last Month', value: 'lastMonth', getRange: DATE_RANGE_GETTERS.lastMonth },
    { label: `2 Months Ago (${twoMonthsAgoMonthName})`, value: 'twoMonthsAgo', getRange: DATE_RANGE_GETTERS.twoMonthsAgo },
    { label: 'Last 7 Days', value: 'last7', getRange: DATE_RANGE_GETTERS.last7 },
    { label: 'Last 30 Days', value: 'last30', getRange: DATE_RANGE_GETTERS.last30 },
    { label: 'Custom Range', value: 'custom', getRange: () => ({ start: null, end: null }) }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Extract unique currencies from transactions
  useEffect(() => {
    if (transactions.length > 0) {
      const currencies = ['all', ...new Set(transactions.map(tx => tx.currency))].filter(Boolean);
      setAvailableCurrencies(currencies);
    }
  }, [transactions]);

  // Fetch user tree data
  useEffect(() => {
    const fetchUserTree = async () => {
      try {
        const response = await getUsersData();
        if (response.success) {
          const tree = await buildTree(response.data, response.data.rootId);
          setUserTree(tree);
        }
      } catch (error) {
        console.error('Error fetching user tree:', error);
      }
    };

    fetchUserTree();
  }, []);

  // Extract unique agents from user tree
  useEffect(() => {
    if (userTree) {
      const extractAgents = (node) => {
        const agents = [];
        if (node) {
          agents.push({
            id: node.username,
            name: node.name,
            username: node.username,
            type: node.type,
            balances: node.balances || {}
          });
          
          if (node.children && Array.isArray(node.children)) {
            node.children.forEach(child => {
              agents.push(...extractAgents(child));
            });
          }
        }
        return agents;
      };

      const allAgents = extractAgents(userTree);
      setAgentsInUserTree(allAgents);
      
      // Debug: Log agent types in the tree
      const agentsByType = allAgents.reduce((acc, agent) => {
        acc[agent.type] = (acc[agent.type] || 0) + 1;
        return acc;
      }, {});
      console.log('📊 Users in tree by type:', agentsByType);
      
      // Debug: Show some players if they exist
      const players = allAgents.filter(a => a.type === 'player');
      if (players.length > 0) {
        console.log(`🎮 Found ${players.length} players in tree. First 3:`, players.slice(0, 3).map(p => p.username));
      }
    }
  }, [userTree]);

  // Handle selectedUser from navigation (e.g., from UserDeepView)
  useEffect(() => {
    if (location.state?.selectedUser && agentsInUserTree.length > 0 && !initialUserSet) {
      const selectedUser = location.state.selectedUser;
      console.log('Setting selected user from navigation:', selectedUser);
      
      // Find the user in the agents tree
      const userAgent = agentsInUserTree.find(agent => agent.username === selectedUser.username);
      if (userAgent) {
        // Set only as From agent to see transactions initiated by this user
        setFromAgent(userAgent);
        // Leave To agent empty to see all recipients
        setToAgent(null);
        setInitialUserSet(true);
        
        // Clear the state to prevent re-selection on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, agentsInUserTree, initialUserSet]);

  const fetchTransactions = async (initiatorFilter = null, targetFilter = null, searchBothDirections = false, currentDateRange = null, currentCustomDateRange = null) => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const userGroups = session.tokens.accessToken.payload['cognito:groups'] || [];
      const isAdmin = userGroups.includes('Admins');
      const isCountry = userGroups.includes('Countries');
      const isCity = userGroups.includes('Cities');
      const isSuperAgent = userGroups.includes('SuperAgents');
      
      // Set user role based on groups
      if (isAdmin) {
        setUserRole('admin');
      } else if (isCountry) {
        setUserRole('country');
      } else if (isCity) {
        setUserRole('city');
      } else if (isSuperAgent) {
        setUserRole('super-agent');
      } else {
        setUserRole('agent');
      }

      // Get user info from session
      const username = session.tokens.accessToken.payload.username || '';
      setUserInfo({ username, isAdmin, isCountry, isCity, isSuperAgent });

      // Calculate date range for the query (use passed parameters or current state)
      const effectiveDateRange = currentDateRange || dateRange;
      const effectiveCustomDateRange = currentCustomDateRange || customDateRange;
      
      const resolvedRange = resolveDateRange(effectiveDateRange, effectiveCustomDateRange);
      const queryStartDate = resolvedRange.start.toISOString();
      const queryEndDate = resolvedRange.end.toISOString();

      console.log("Date range for query:", { 
        startDate: queryStartDate, 
        endDate: queryEndDate,
        effectiveDateRange,
        effectiveCustomDateRange,
        dateRangeState: dateRange,
        customDateRangeState: customDateRange
      });

      // Try the REST API endpoint
      try {
        console.log("Fetching transactions from querytransact API...");
        
        let allTransactions = [];
        
        // If searchBothDirections is true, we need to make multiple API calls
        if (searchBothDirections && (initiatorFilter || targetFilter)) {
          const agentUsername = initiatorFilter || targetFilter;
          console.log(`Searching for transactions where ${agentUsername} is either initiator or target`);
          
          // Fetch transactions where agent is initiator
          const queryParams1 = new URLSearchParams();
          queryParams1.append('initiator', agentUsername);
          // Add date parameters (backend needs to be updated to support these)
          if (queryStartDate) queryParams1.append('startDate', queryStartDate);
          if (queryEndDate) queryParams1.append('endDate', queryEndDate);
          const path1 = `/querytransact?${queryParams1.toString()}`;
          
          console.log(`Fetching initiator transactions from: ${path1}`);
          const result1 = await get({
            apiName: 'querytransact',
            path: path1
          }).response;
          const data1 = await result1.body.json();
          
          // Fetch transactions where agent is target
          const queryParams2 = new URLSearchParams();
          queryParams2.append('targetAgent', agentUsername);
          // Add date parameters (backend now supports these)
          if (queryStartDate) queryParams2.append('startDate', queryStartDate);
          if (queryEndDate) queryParams2.append('endDate', queryEndDate);
          const path2 = `/querytransact?${queryParams2.toString()}`;
          
          console.log(`Fetching target transactions from: ${path2}`);
          const result2 = await get({
            apiName: 'querytransact',
            path: path2
          }).response;
          const data2 = await result2.body.json();
          
          // Combine and deduplicate transactions
          const transactionMap = new Map();
          
          // Handle response - should be array directly
          const items1 = Array.isArray(data1) ? data1 : [];
          const items2 = Array.isArray(data2) ? data2 : [];
          
          if (data1.error) {
            console.error('Error in data1:', data1);
          } else {
            items1.forEach(tx => transactionMap.set(tx.id, tx));
          }
          
          if (data2.error) {
            console.error('Error in data2:', data2);
          } else {
            items2.forEach(tx => transactionMap.set(tx.id, tx));
          }
          
          allTransactions = Array.from(transactionMap.values());
          console.log(`Found ${allTransactions.length} total transactions for ${agentUsername}`);
          
        } else {
          // Normal filtering with specific initiator and/or target
          const queryParams = new URLSearchParams();
          if (initiatorFilter) {
            queryParams.append('initiator', initiatorFilter);
          }
          if (targetFilter) {
            queryParams.append('targetAgent', targetFilter);
          }
          // Add date parameters (backend now supports these)
          if (queryStartDate) queryParams.append('startDate', queryStartDate);
          if (queryEndDate) queryParams.append('endDate', queryEndDate);
          
          const queryString = queryParams.toString();
          const path = queryString ? `/querytransact?${queryString}` : '/querytransact';
          
          console.log(`Fetching from path: ${path}`);
          
          const restResult = await get({
            apiName: 'querytransact',
            path: path
          }).response;
          
          const data = await restResult.body.json();
          console.log('API result:', data);
          
          // Check if it's an error response
          if (data.error) {
            console.error('API returned error:', data);
            // If no filters provided, default to current user
            if (data.error.includes('Query parameters required') && username) {
              console.log('No filters provided, defaulting to current user:', username);
              return fetchTransactions(username, null, true, effectiveDateRange, effectiveCustomDateRange);
            }
            setError(data.message || data.error);
            setTransactions([]);
            return;
          } else {
            // Handle response - should be array directly
            allTransactions = Array.isArray(data) ? data : [];
            
            // Check if transactions are in the future
            if (allTransactions.length > 0) {
              const now = new Date();
              const futureTransactions = allTransactions.filter(tx => 
                new Date(tx.timestamp) > now
              );
              
              if (futureTransactions.length === allTransactions.length) {
                console.warn(`⚠️ All ${allTransactions.length} transactions are dated in the future`);
                const earliestDate = new Date(Math.min(...allTransactions.map(tx => new Date(tx.timestamp))));
                console.info(`📅 Earliest transaction date: ${earliestDate.toLocaleDateString()}`);
                setFutureDatedWarning({
                  type: 'all',
                  count: allTransactions.length,
                  earliestDate: earliestDate.toLocaleDateString()
                });
              } else if (futureTransactions.length > 0) {
                console.warn(`⚠️ ${futureTransactions.length} of ${allTransactions.length} transactions are dated in the future`);
                setFutureDatedWarning({
                  type: 'some',
                  count: futureTransactions.length,
                  total: allTransactions.length
                });
              } else {
                setFutureDatedWarning(null);
              }
            } else {
              setFutureDatedWarning(null);
            }
            console.log(`Received ${allTransactions.length} transactions from API`);
          }
        }
        
        // Store the transactions
        console.log('Total transactions fetched:', allTransactions.length);
        
        // Log timestamp debug info
        if (allTransactions.length > 0) {
          console.log('=== Transaction Timestamp Debug ===');
          console.log('First transaction timestamp:', allTransactions[0].timestamp);
          console.log('First transaction date:', new Date(allTransactions[0].timestamp).toISOString());
          console.log('Last transaction timestamp:', allTransactions[allTransactions.length - 1].timestamp);
          console.log('Last transaction date:', new Date(allTransactions[allTransactions.length - 1].timestamp).toISOString());
          console.log('Current browser time:', new Date().toISOString());
          console.log('Browser timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
          console.log('===================================');
        }
        
        setTransactions(allTransactions);
        
      } catch (apiError) {
        console.error('API Error:', apiError);
        setError('Failed to fetch transactions: ' + apiError.message);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to fetch transactions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Update the filter logic in useEffect
  useEffect(() => {
    if (transactions.length > 0) {
      let filtered = [...transactions];
      
      // Apply date range filter
      if (dateRange === 'custom' && customDateRange) {
        const { start, end } = customDateRange;
        if (start && end) {
          filtered = filtered.filter(tx => {
            const txDate = new Date(tx.timestamp);
            return txDate >= startOfDay(start) && txDate <= endOfDay(end);
          });
        }
      } else if (dateRange !== 'all') {
        const range = quickRanges.find(r => r.value === dateRange);
        if (range) {
          const { start, end } = range.getRange();
          filtered = filtered.filter(tx => {
            const txDate = new Date(tx.timestamp);
            return txDate >= start && txDate <= end;
          });
        }
      }
      
      // Apply type filter
      if (filters.type !== 'all') {
        filtered = filtered.filter(tx => {
          if (filters.type === 'deposit') {
            return tx.isWithdrawal === false;
          } else if (filters.type === 'withdrawal') {
            return tx.isWithdrawal === true;
          }
          return true;
        });
      }
      
      // Apply currency filter
      if (filters.currency !== 'all') {
        filtered = filtered.filter(tx => tx.currency === filters.currency);
      }
      
      // Apply target type filter (agent vs player)
      if (filters.targetType !== 'all') {
        filtered = filtered.filter(tx => {
          // Find the target user in the tree
          const targetUser = agentsInUserTree.find(user => user.username === tx.targetAgent);
          // Check if target is an agent (not a player) - agents include country, city, super-agent, agent
          const targetIsAgent = targetUser && targetUser.type !== 'player';
          
          if (filters.targetType === 'agent') {
            return targetIsAgent;
          } else if (filters.targetType === 'player') {
            // Either it's a player in the tree, or not in the tree at all
            return !targetIsAgent;
          }
          return true;
        });
      }
      
      // Apply authority type filter (higher authority)
      if (filters.authorityType === 'higherAuthority' && userRole) {
        filtered = filtered.filter(tx => {
          // Find initiator and target in the tree
          const initiatorUser = agentsInUserTree.find(user => user.username === tx.initiator);
          const targetUser = agentsInUserTree.find(user => user.username === tx.targetAgent);
          
          // Check if initiator is higher authority:
          // 1. Not found in tree (outside user's hierarchy) = higher authority
          // 2. Found in tree AND has higher rank
          const initiatorIsHigherAuthority = !initiatorUser || isHigherRank(initiatorUser.type, userRole);
          
          // Check if target is higher authority:
          // 1. Not found in tree (outside user's hierarchy) = higher authority
          // 2. Found in tree AND has higher rank
          const targetIsHigherAuthority = !targetUser || isHigherRank(targetUser.type, userRole);
          
          // Return true if either party is a higher authority
          return initiatorIsHigherAuthority || targetIsHigherAuthority;
        });
      }
      
      // Only apply user tree filter for cities, super-agents, and agents
      // BUT NOT when viewing a specific agent's transactions (fromAgent or toAgent is set)
      if (userInfo && (userRole === 'city' || userRole === 'super-agent' || userRole === 'agent')) {
        // Skip this filter if we're specifically viewing another user's transactions
        if (!fromAgent && !toAgent) {
          filtered = filtered.filter(tx => 
            tx.initiator === userInfo.username || tx.targetAgent === userInfo.username
          );
        }
      }
      
      // Apply agent filters only when both are selected (specific From→To filtering)
      // When only one is selected, we already fetched bidirectional results from API
      if (fromAgent && toAgent) {
        // Both filters selected: apply specific From→To filtering
        filtered = filtered.filter(tx => 
          tx.initiator === fromAgent.username && tx.targetAgent === toAgent.username
        );
      }
      // Note: When only one agent is selected, we don't filter here because
      // the API already returned all transactions where that agent is either initiator or target

      // Quick text search across initiator, target, notes, amount, currency, id
      if (searchQuery && searchQuery.trim().length > 0) {
        const q = searchQuery.trim().toLowerCase();
        filtered = filtered.filter(tx => {
          const fields = [
            tx.initiator,
            tx.targetAgent,
            tx.notes,
            tx.currency,
            tx.id,
            String(tx.amount ?? '')
          ];
          return fields.some(f => f && String(f).toLowerCase().includes(q));
        });
      }

      // Sort data
      filtered.sort((a, b) => {
        if (sortConfig.key === 'timestamp') {
          return sortConfig.direction === 'asc'
            ? new Date(a.timestamp) - new Date(b.timestamp)
            : new Date(b.timestamp) - new Date(a.timestamp);
        } else if (sortConfig.key === 'amount') {
          return sortConfig.direction === 'asc'
            ? a.amount - b.amount
            : b.amount - a.amount;
        }
        return 0;
      });
      
      setFilteredTransactions(filtered);
      const newTotalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
      setTotalPages(newTotalPages);
      
      // Only reset to page 1 if the number of pages has changed
      if (newTotalPages < currentPage) {
        setCurrentPage(1);
      }
    }
  }, [transactions, dateRange, customDateRange, filters, sortConfig, userInfo, userRole, fromAgent, toAgent, ITEMS_PER_PAGE, searchQuery, agentsInUserTree]);

  // Track if initial load is complete
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize immediately with session username as fallback AND set userRole
  useEffect(() => {
    const initializeImmediately = async () => {
      try {
        // First, get session and set userRole immediately
        const session = await fetchAuthSession();
        const userGroups = session.tokens.accessToken.payload['cognito:groups'] || [];
        const isAdmin = userGroups.includes('Admins');
        const isCountry = userGroups.includes('Countries');
        const isCity = userGroups.includes('Cities');
        const isSuperAgent = userGroups.includes('SuperAgents');
        
        // Set user role based on groups - do this FIRST
        if (isAdmin) {
          setUserRole('admin');
        } else if (isCountry) {
          setUserRole('country');
        } else if (isCity) {
          setUserRole('city');
        } else if (isSuperAgent) {
          setUserRole('super-agent');
        } else {
          setUserRole('agent');
        }
        
        console.log('✅ User role set early:', isAdmin ? 'admin' : isCountry ? 'country' : isCity ? 'city' : isSuperAgent ? 'super-agent' : 'agent');
        
        // Skip auto-initialization of fromAgent if we're expecting a selectedUser from navigation
        if (location.state?.selectedUser) {
          console.log('Skipping auto-initialization - waiting for selectedUser from navigation');
          setIsInitialized(true);
          return;
        }
        
        if (!isInitialized && !fromAgent) {
          const username = session.tokens.accessToken.payload.username || '';
          
          if (username) {
            console.log('Immediate initialization with session username:', username);
            // Set a temporary fromAgent with just the username
            setFromAgent({ 
              username, 
              name: username, 
              id: username,
              type: 'agent' // default type
            });
            setIsInitialized(true);
            
            // Then try to get more complete data
            try {
              const selfData = await fetchSelf();
              if (selfData && selfData.username) {
                // Update with more complete data if available
                setFromAgent(prev => ({
                  ...prev,
                  name: selfData.name || selfData.username,
                  type: selfData.type || 'agent',
                  ...selfData
                }));
              }
            } catch (selfError) {
              console.error('Error fetching self data:', selfError);
              // Continue with basic data
            }
          }
        }
      } catch (error) {
        console.error('Error in immediate initialization:', error);
      }
    };

    initializeImmediately();
  }, [location.state]); // Run once on mount and when navigation state changes

  // Update fromAgent when user tree loads with complete data
  useEffect(() => {
    if (fromAgent && agentsInUserTree.length > 0) {
      const currentUserAgent = agentsInUserTree.find(agent => agent.username === fromAgent.username);
      if (currentUserAgent) {
        console.log('Updating fromAgent with complete tree data:', currentUserAgent);
        // Update with complete agent data from tree
        setFromAgent(currentUserAgent);
      }
    }
  }, [agentsInUserTree]);

  // Fetch transactions when filters change (including initial set)
  useEffect(() => {
    // Only fetch if we have initialized and have at least one filter
    if (isInitialized && (fromAgent || toAgent)) {
      // Determine if we should search both directions
      // If only one agent is selected (either From OR To, but not both), search both directions
      const searchBothDirections = (fromAgent && !toAgent) || (!fromAgent && toAgent);
      
      console.log('Fetching transactions with filters:', { 
        fromAgent: fromAgent?.username, 
        toAgent: toAgent?.username,
        searchBothDirections,
        dateRange,
        customDateRange
      });
      
      fetchTransactions(fromAgent?.username, toAgent?.username, searchBothDirections, dateRange, customDateRange);
    }
  }, [fromAgent, toAgent, isInitialized, dateRange, customDateRange]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const paginatedTransactions = useMemo(() => {
    return filteredTransactions.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredTransactions, currentPage]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Ensure at least one filter is provided
      if (fromAgent?.username || toAgent?.username) {
        // Use bidirectional search if only one agent is selected
        const searchBothDirections = (fromAgent && !toAgent) || (!fromAgent && toAgent);
        await fetchTransactions(fromAgent?.username, toAgent?.username, searchBothDirections, dateRange, customDateRange);
      } else if (userInfo?.username) {
        // Default to current user searching both directions
        await fetchTransactions(userInfo.username, null, true, dateRange, customDateRange);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Compute a human-readable string for the active date range (for PDF header)
  const getDateRangeLabel = () => {
    if (dateRange === 'custom' && customDateRange?.start && customDateRange?.end) {
      return `${format(customDateRange.start, 'MMM d, yyyy')} - ${format(customDateRange.end, 'MMM d, yyyy')}`;
    }
    const range = quickRanges.find(r => r.value === dateRange);
    if (range) {
      const { start, end } = range.getRange();
      if (start && end) return `${range.label} (${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')})`;
      return range.label;
    }
    return 'All Time';
  };

  // Build a short summary of active filters for the PDF header
  const buildActiveFiltersSummary = () => {
    const parts = [];
    if (fromAgent) parts.push(`From: @${fromAgent.username}`);
    if (toAgent) parts.push(`To: @${toAgent.username}`);
    if (filters.type !== 'all') parts.push(`Type: ${filters.type}`);
    if (filters.currency !== 'all') parts.push(`Currency: ${filters.currency}`);
    if (filters.targetType !== 'all') parts.push(`Target: ${filters.targetType}`);
    if (filters.authorityType === 'higherAuthority') parts.push('Higher authority only');
    if (searchQuery) parts.push(`Search: "${searchQuery}"`);
    return parts.length ? parts.join('  |  ') : 'None';
  };

  const handleExportPDF = async () => {
    if (!filteredTransactions.length || isExporting) return;
    setIsExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const generatedAt = format(new Date(), 'yyyy-MM-dd HH:mm');

      // Title block
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Transaction Log', 40, 40);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(90);
      doc.text(`Generated: ${generatedAt}`, pageWidth - 40, 40, { align: 'right' });

      doc.setTextColor(40);
      doc.setFontSize(10);
      doc.text(`Period: ${getDateRangeLabel()}`, 40, 60);
      doc.text(`Total transactions: ${filteredTransactions.length}`, 40, 76);

      const filtersLine = `Filters: ${buildActiveFiltersSummary()}`;
      const filtersWrapped = doc.splitTextToSize(filtersLine, pageWidth - 80);
      doc.text(filtersWrapped, 40, 92);

      let cursorY = 92 + filtersWrapped.length * 12 + 8;

      // Per-currency totals block
      const currencies = Object.keys(summaryData.byCurrency);
      if (currencies.length > 0) {
        const totalsRows = currencies.map(c => {
          const d = summaryData.byCurrency[c];
          return [
            c,
            String(d.depositCount),
            formatAmount(d.deposits, c),
            String(d.withdrawalCount),
            formatAmount(d.withdrawals, c),
            `${d.net >= 0 ? '+' : ''}${formatAmount(d.net, c)}`
          ];
        });

        autoTable(doc, {
          startY: cursorY,
          head: [['Currency', '# Deposits', 'Deposits', '# Withdrawals', 'Withdrawals', 'Net']],
          body: totalsRows,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 4 },
          headStyles: { fillColor: [55, 65, 81], textColor: 255 },
          margin: { left: 40, right: 40 },
        });
        cursorY = doc.lastAutoTable.finalY + 16;
      }

      // Sort transactions chronologically (oldest first) for the log
      const rows = [...filteredTransactions]
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .map(tx => [
          format(new Date(tx.timestamp), 'yyyy-MM-dd HH:mm'),
          tx.isWithdrawal ? 'Withdrawal' : 'Deposit',
          tx.initiator || '',
          tx.targetAgent || '',
          tx.currency || '',
          `${tx.isWithdrawal ? '-' : '+'}${formatAmount(tx.amount, tx.currency || 'USD')}`,
          tx.notes || ''
        ]);

      autoTable(doc, {
        startY: cursorY,
        head: [['Date', 'Type', 'From', 'To', 'Currency', 'Amount', 'Notes']],
        body: rows,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [55, 65, 81], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 90 },
          1: { cellWidth: 60 },
          2: { cellWidth: 100 },
          3: { cellWidth: 100 },
          4: { cellWidth: 55 },
          5: { cellWidth: 90, halign: 'right' },
          6: { cellWidth: 'auto' },
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const v = data.cell.raw;
            if (v === 'Withdrawal') data.cell.styles.textColor = [185, 28, 28];
            else data.cell.styles.textColor = [4, 120, 87];
          }
          if (data.section === 'body' && data.column.index === 5) {
            const v = String(data.cell.raw || '');
            if (v.startsWith('-')) data.cell.styles.textColor = [185, 28, 28];
            else if (v.startsWith('+')) data.cell.styles.textColor = [4, 120, 87];
          }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const page = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text(`Page ${page} of ${pageCount}`, pageWidth - 40, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
        },
        margin: { left: 40, right: 40, bottom: 40 },
      });

      const filenameParts = ['transactions'];
      if (fromAgent) filenameParts.push(`from-${fromAgent.username}`);
      if (toAgent) filenameParts.push(`to-${toAgent.username}`);
      filenameParts.push(format(new Date(), 'yyyyMMdd-HHmm'));
      doc.save(`${filenameParts.join('_')}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  // Active filter count for the filter toggle badge.
  // Agent pickers, time period, and search are always visible, so they don't count toward the badge.
  const activeFilterCount = (
    (filters.type !== 'all' ? 1 : 0) +
    (filters.currency !== 'all' ? 1 : 0) +
    (filters.targetType !== 'all' ? 1 : 0) +
    (filters.authorityType !== 'all' ? 1 : 0)
  );

  const { t } = useTranslation();

  // Per-currency totals (deposits, withdrawals, net) used by HeroSummary
  // and the PDF export. Kept simple now that the directional breakdown is gone.
  const summaryData = useMemo(() => {
    const byCurrency = {};
    filteredTransactions.forEach((tx) => {
      const currency = tx.currency || 'USD';
      if (!byCurrency[currency]) {
        byCurrency[currency] = {
          deposits: 0,
          withdrawals: 0,
          depositCount: 0,
          withdrawalCount: 0,
          net: 0,
        };
      }
      if (tx.isWithdrawal) {
        byCurrency[currency].withdrawals += tx.amount;
        byCurrency[currency].withdrawalCount++;
        byCurrency[currency].net -= tx.amount;
      } else {
        byCurrency[currency].deposits += tx.amount;
        byCurrency[currency].depositCount++;
        byCurrency[currency].net += tx.amount;
      }
    });
    return { byCurrency };
  }, [filteredTransactions]);

  // The period currently being reported on, shared with the settlement audit.
  const selectedPeriod = useMemo(
    () => resolveDateRange(dateRange, customDateRange),
    [dateRange, customDateRange]
  );

  const selectedPeriodLabel = useMemo(
    () => `${format(selectedPeriod.start, 'MMM d')} – ${format(selectedPeriod.end, 'MMM d, yyyy')}`,
    [selectedPeriod]
  );

  // Scoped to a closed month: mid-period ranges are always full of bets that
  // have not settled yet, so the check is only meaningful once the month is
  // over. The agent under review also needs a small enough player base to query
  // in one go. The target agent takes priority; when only one picker is set the
  // page searches both directions, so that agent is a target too.
  const settlementAuditTarget = useMemo(() => {
    if (dateRange !== 'lastMonth') return null;

    const candidate = toAgent || fromAgent;
    if (!candidate || !userTree) return null;
    if (candidate.type !== 'agent' && candidate.type !== 'super-agent') return null;

    const node = findNodeInTree(userTree, candidate.username);
    if (!node) return null;

    const players = getAllPlayersUnderNode(node);
    if (players.length === 0 || players.length > MAX_AUDIT_PLAYERS) return null;

    return { agent: candidate, players };
  }, [dateRange, toAgent, fromAgent, userTree]);

  return (
    <div className="space-y-4 p-4">
      {/* Future-dated transactions warning */}
      {futureDatedWarning && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t('transactions.futureDateWarning', 'Future-Dated Transactions Detected')}
              </h3>
              <div className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                {futureDatedWarning.type === 'all' ? (
                  <>
                    {t('transactions.allFutureDated', `All ${futureDatedWarning.count} transactions are dated in the future.`)}
                    {' '}
                    {t('transactions.earliestDate', `The earliest transaction date is ${futureDatedWarning.earliestDate}.`)}
                    {' '}
                    {dateRange !== 'allTime' && t('transactions.tryAllTime', 'Try selecting "All Time" to see all transactions.')}
                  </>
                ) : (
                  <>
                    {t('transactions.someFutureDated', `${futureDatedWarning.count} of ${futureDatedWarning.total} transactions are dated in the future.`)}
                    {' '}
                    {dateRange !== 'allTime' && t('transactions.tryAllTime', 'Try selecting "All Time" to see all transactions.')}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header toolbar */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">
          {t('transactions.history', 'Transaction History')}
        </h1>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setFiltersOpen(o => !o)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
            title={t('transactions.filters', 'Filters')}
            aria-label={t('transactions.filters', 'Filters')}
          >
            <AdjustmentsHorizontalIcon className="w-5 h-5" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-semibold flex items-center justify-center bg-primary-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={isExporting || filteredTransactions.length === 0}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
            title={isExporting ? t('transactions.exporting', 'Preparing PDF...') : t('transactions.exportPdf', 'Download PDF')}
            aria-label={t('transactions.exportPdf', 'Download PDF')}
          >
            <DocumentArrowDownIcon className={`w-5 h-5 ${isExporting ? 'animate-pulse' : ''}`} />
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('transactions.refresh', 'Refresh transactions')}
            aria-label={t('common.refresh', 'Refresh')}
          >
            <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Quick search */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          inputMode="search"
          placeholder={t('transactions.quickSearchPlaceholder', 'Search agent, notes, amount...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="block w-full pl-9 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
            aria-label={t('common.clear', 'Clear search')}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Always-visible time period + agent pickers (most-used filters on mobile) */}
      <div className="space-y-2 sm:space-y-0 sm:flex sm:items-stretch sm:gap-2">
        <div className="sm:w-56 sm:flex-shrink-0">
          <DateRangePicker
            value={dateRange}
            onChange={(value) => {
              if (typeof value === 'string') {
                setDateRange(value);
                setCustomDateRange(null);
              } else {
                setDateRange('custom');
                setCustomDateRange(value);
              }
            }}
          />
        </div>
        <div className="flex items-stretch gap-1.5 sm:flex-1">
          <div className="flex-1 min-w-0">
            <AgentFilter
              label={t('transactions.fromAgent', 'From Agent')}
              value={fromAgent}
              agents={agentsInUserTree}
              onChange={setFromAgent}
              onClear={() => {
                if (!toAgent && userInfo?.username) {
                  const currentUserAgent = agentsInUserTree.find(agent => agent.username === userInfo.username);
                  if (currentUserAgent) {
                    setFromAgent(currentUserAgent);
                  } else {
                    setFromAgent({ username: userInfo.username, name: userInfo.username });
                  }
                } else {
                  setFromAgent(null);
                }
              }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <AgentFilter
              label={t('transactions.toAgent', 'To Agent')}
              value={toAgent}
              agents={agentsInUserTree}
              onChange={setToAgent}
              onClear={() => {
                if (!fromAgent && userInfo?.username) {
                  const currentUserAgent = agentsInUserTree.find(agent => agent.username === userInfo.username);
                  if (currentUserAgent) {
                    setFromAgent(currentUserAgent);
                  } else {
                    setFromAgent({ username: userInfo.username, name: userInfo.username });
                  }
                  setToAgent(null);
                } else {
                  setToAgent(null);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Sports bets that straddle this period and shift its GGR */}
      {settlementAuditTarget && (
        <button
          onClick={() => setSettlementAuditOpen(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors text-left"
        >
          <ClockIcon className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-amber-900 dark:text-amber-200">
              {t('settlementAudit.button', 'Late sports payouts')}
            </span>
            <span className="block text-xs text-amber-700 dark:text-amber-300 truncate">
              {t('settlementAudit.buttonHint', {
                count: settlementAuditTarget.players.length
              }, 'Bets that cross this period')}
            </span>
          </span>
          <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        </button>
      )}

      {/* Filters */}
      <div className={`space-y-4 ${filtersOpen ? '' : 'hidden'}`}>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="w-full flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Filters</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                  {filteredTransactions.length} results
                </span>
              </div>
              <button
                onClick={async () => {
                  setFilters({
                    type: 'all',
                    dateRange: 'all',
                    currency: 'all',
                    targetType: 'all',
                    authorityType: 'all'
                  });
                  setDateRange('month'); // Reset to default date range
                  setCustomDateRange(null);
                  
                  // Reset to default filter (current user, searching both directions)
                  if (userInfo?.username && agentsInUserTree.length > 0) {
                    const currentUserAgent = agentsInUserTree.find(agent => agent.username === userInfo.username);
                    if (currentUserAgent) {
                      setFromAgent(currentUserAgent);
                      setToAgent(null); // This will trigger bidirectional search
                    } else {
                      // If current user not in tree, just set username
                      setFromAgent({ username: userInfo.username, name: userInfo.username });
                      setToAgent(null); // This will trigger bidirectional search
                    }
                  }
                }}
                className="text-xs px-3 py-1.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg transition-all duration-200 flex items-center gap-1.5"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Reset Filters
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
              {/* Transaction Type Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Transaction Type</label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  <div className="flex gap-2 min-w-max">
                    <button
                      onClick={() => handleFilterChange('type', 'all')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                        filters.type === 'all'
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => handleFilterChange('type', 'deposit')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                        filters.type === 'deposit'
                          ? 'bg-emerald-500 text-white shadow-sm'
                          : 'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300'
                      }`}
                    >
                      Deposits
                    </button>
                    <button
                      onClick={() => handleFilterChange('type', 'withdrawal')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                        filters.type === 'withdrawal'
                          ? 'bg-red-500 text-white shadow-sm'
                          : 'bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300'
                      }`}
                    >
                      Withdrawals
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Currency Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Currency</label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                  <div className="flex gap-2 min-w-max">
                    {availableCurrencies.map(currency => (
                      <button
                        key={currency}
                        onClick={() => handleFilterChange('currency', currency)}
                        className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                          filters.currency === currency
                            ? 'bg-primary-500 text-white shadow-sm'
                            : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {currency === 'all' ? 'All Currencies' : currency}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Target Type Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Target Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFilterChange('targetType', 'all')}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                      filters.targetType === 'all'
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => handleFilterChange('targetType', 'agent')}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      filters.targetType === 'agent'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                    }`}
                  >
                    <UserGroupIcon className="w-4 h-4" />
                    To Agent
                  </button>
                  <button
                    onClick={() => handleFilterChange('targetType', 'player')}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      filters.targetType === 'player'
                        ? 'bg-purple-500 text-white shadow-sm'
                        : 'bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                    }`}
                  >
                    <UserIcon className="w-4 h-4" />
                    To Player
                  </button>
                </div>
              </div>

              {/* Authority Type Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Authority Level</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFilterChange('authorityType', 'all')}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                      filters.authorityType === 'all'
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => handleFilterChange('authorityType', 'higherAuthority')}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5 ${
                      filters.authorityType === 'higherAuthority'
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                    }`}
                  >
                    <BuildingOfficeIcon className="w-4 h-4" />
                    Higher Authority
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Hero summary - the headline numbers users come here for */}
      <HeroSummary
        t={t}
        byCurrency={summaryData.byCurrency}
        activeCurrency={
          filters.currency !== 'all'
            ? filters.currency
            : heroCurrency || Object.keys(summaryData.byCurrency)[0]
        }
        onCurrencyChange={(c) => setHeroCurrency(c)}
        typeFilter={filters.type}
        onTypeFilterChange={(v) => handleFilterChange('type', v)}
        targetTypeFilter={filters.targetType}
        onTargetTypeFilterChange={(v) => handleFilterChange('targetType', v)}
        totalCount={filteredTransactions.length}
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        {!isInitialized || loading ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            {!isInitialized ? t('common.initializing', 'Initializing...') : t('common.loading', 'Loading transactions...')}
          </div>
        ) : paginatedTransactions.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            {t('transactions.noTransactions', 'No transactions found')}
            {futureDatedWarning && dateRange !== 'allTime' && (
              <div className="mt-2 text-sm">
                {t('transactions.tryDifferentDateRange', 'Try selecting a different date range or "All Time" to see transactions.')}
              </div>
            )}
          </div>
        ) : (
          paginatedTransactions.map((tx) => (
            <TransactionCard 
              key={tx.id} 
              transaction={tx} 
              onClick={setSelectedTransaction}
              agentsInUserTree={agentsInUserTree}
              userRole={userRole}
            />
          ))
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="text-sm text-gray-700 dark:text-gray-300 w-full sm:w-auto text-center sm:text-left">
          {t('transactions.showing', 'Showing')} {paginatedTransactions.length} {t('transactions.of', 'of')} {filteredTransactions.length} {t('transactions.transactions', 'transactions')}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {selectedTransaction && (
        <TransactionModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          agentsInUserTree={agentsInUserTree}
          userRole={userRole}
        />
      )}

      {settlementAuditOpen && settlementAuditTarget && (
        <SportsSettlementAuditModal
          agent={settlementAuditTarget.agent}
          players={settlementAuditTarget.players}
          rangeStart={selectedPeriod.start}
          rangeEnd={selectedPeriod.end}
          rangeLabel={selectedPeriodLabel}
          onClose={() => setSettlementAuditOpen(false)}
        />
      )}
    </div>
  );
};

export default withAuthenticator(Transactions, {
  hideSignUp: true,
  signUpAttributes: [],
  loginMechanisms: ['username']
}); 